// Multiple local accounts (vaults) per machine. Each account is a fully independent encrypted vault — its
// own DEK, DB, blobs, keychain entry, and (optionally) its own linked aicharactercards.com account. Vaults
// are already isolated by their DEK; this layer just lets more than one exist side by side and picks which
// one a session opens.
//
// The registry itself is UNENCRYPTED — it must be readable before any vault unlocks, to render the picker.
// It leaks only how many accounts exist and their display names. Nothing sensitive (no keys, no data).
//
// BACK-COMPAT: the pre-multi-account install had one vault at fixed names (aicc-account.json,
// aicc-data-dir.txt, keychain "dek-v1", Documents/rin-chat). That vault becomes the reserved "default"
// account, which KEEPS those exact names — so existing users are untouched and never re-onboard. Only
// additional accounts get id-suffixed names.
import { t } from "@lingui/core/macro";
import { appLocalDataDir, documentDir, join } from "@tauri-apps/api/path";
import { invoke } from "./rpc";

/** Reserved id for the legacy/first account — keeps the original fixed filenames + keychain entry. */
export const DEFAULT_ACCOUNT_ID = "default";

/** The folder that holds every account's data, always named this, wherever the base is. */
export const RIN_CHAT_DIR = "rin-chat";

export interface AccountEntry {
  id: string; // STABLE internal identity (random). Names the account file / keychain / pointer; survives rename.
  username: string; // display + data-folder name + per-machine uniqueness key
  basePath: string; // dir CONTAINING the rin-chat folder; "" = the OS Documents dir (the default)
  createdAt: number;
  lastUsed: number;
}
interface Registry {
  version: number;
  lastUsedId: string | null;
  accounts: AccountEntry[];
}

// --- Pure path logic (unit-tested; NO Tauri calls so it runs in vitest) ---------------------------------

const trimTrailingSep = (p: string): string => p.replace(/[\\/]+$/, "");
const sepOf = (p: string): string => (p.includes("\\") ? "\\" : "/");
const baseNameOf = (p: string): string => {
  const t = trimTrailingSep(p);
  const i = Math.max(t.lastIndexOf("/"), t.lastIndexOf("\\"));
  return i >= 0 ? t.slice(i + 1) : t;
};
const dirNameOf = (p: string): string => {
  const t = trimTrailingSep(p);
  const i = Math.max(t.lastIndexOf("/"), t.lastIndexOf("\\"));
  return i >= 0 ? t.slice(0, i) : "";
};

/** A filesystem-safe folder name for a username. Odd characters collapse to "_"; the display username is
 *  kept as-is in the registry. `usernameKey` (lowercased) is the uniqueness/collision key so "Alice" and
 *  "alice" can't produce two folders that collide on a case-insensitive filesystem. */
export function folderSafe(username: string): string {
  const cleaned = String(username ?? "").trim().replace(/[^\w \-.]+/g, "_").replace(/\s+/g, " ").trim().slice(0, 40);
  // Dot-only names survive the character filter but are PATH SYNTAX: a user named ".." would put the
  // vault in the parent folder — and a later reset would delete the user's own Documents subfolders.
  return /^\.+$/.test(cleaned) ? "account" : cleaned || "account";
}
export const usernameKey = (username: string): string => folderSafe(username).toLowerCase();

/** The directory that CONTAINS the rin-chat folder. If the user picked the rin-chat folder itself (or a
 *  path ending in it), use its parent — so both `X:/AI` and `X:/AI/rin-chat` resolve to base `X:/AI`. */
export function normalizeBase(picked: string): string {
  const t = trimTrailingSep(picked);
  return baseNameOf(t).toLowerCase() === RIN_CHAT_DIR ? dirNameOf(t) : t;
}

/** Base for an UNMIGRATED account, derived from where its data lives now. Recognizes BOTH layouts: a flat
 *  legacy root (`X:` or `X:/rin-chat` → strip a trailing rin-chat) and an ALREADY-NESTED root
 *  (`X:/rin-chat/{username}` → the grandparent). The nested case matters when the registry is lost but the
 *  pointer survives (re-registered with an empty basePath): deriving from the nested path itself computes
 *  `{nested}/rin-chat/{username}` and physically moves the vault a level deeper on every such loss. */
export function deriveBaseFrom(oldRoot: string, username: string): string {
  const t = trimTrailingSep(oldRoot);
  if (baseNameOf(t).toLowerCase() === folderSafe(username).toLowerCase()) {
    const parent = dirNameOf(t);
    if (baseNameOf(parent).toLowerCase() === RIN_CHAT_DIR) return dirNameOf(parent);
  }
  return normalizeBase(t);
}

/** `{base}/rin-chat/{folderSafe(username)}` — pure; `base` must be concrete (caller supplies Documents for ""). */
export function dataRootFrom(base: string, username: string): string {
  const b = trimTrailingSep(base);
  const sep = sepOf(b || "/");
  return [b, RIN_CHAT_DIR, folderSafe(username)].filter(Boolean).join(sep);
}

/** Path equality for comparing a user-picked folder against a derived one: separator- and case-insensitive,
 *  because the native picker returns `H:\rin-chat\AICC` while `dataRootFrom` builds `H:/rin-chat/AICC`
 *  (`sepOf` sees no backslash in a bare `H:`). Windows/macOS filesystems are case-insensitive anyway. */
const samePath = (a: string, b: string): boolean =>
  trimTrailingSep(a).replace(/\\/g, "/").toLowerCase() === trimTrailingSep(b).replace(/\\/g, "/").toLowerCase();

/** Candidate data roots for a folder the user picked while relocating a vault that moved (drive letter
 *  changed, folder renamed). Ordered most-certain first; the caller probes each for a real DB and takes the
 *  first hit. Two shapes are accepted because both are natural picks:
 *    - the vault folder ITSELF (`H:/rin-chat/AICC`) — only when its grandparent re-derives to exactly it,
 *      which is what proves the folder belongs to THIS username rather than a sibling account;
 *    - a base (`H:`) or the rin-chat parent (`H:/rin-chat`) — `{base}/rin-chat/{username}`.
 *  `base` is returned alongside `root` because it MUST be recorded on the account: the boot layout
 *  migration recomputes the root from basePath, and a stale base would move the data straight back out. */
export function relocateCandidates(picked: string, username: string): Array<{ root: string; base: string }> {
  const out: Array<{ root: string; base: string }> = [];
  const parentBase = normalizeBase(dirNameOf(picked));
  if (parentBase && samePath(dataRootFrom(parentBase, username), picked)) {
    out.push({ root: trimTrailingSep(picked), base: parentBase });
  }
  const asBase = normalizeBase(picked);
  out.push({ root: dataRootFrom(asBase, username), base: asBase });
  // Last resort: the picked folder ITSELF, whatever it's named — a RENAMED vault folder matches neither
  // shape above, yet the panel promises rename recovery. Safe because the caller only accepts a candidate
  // that actually contains aicc.db, and the next boot's layout migration re-canonicalizes the folder to
  // {base}/rin-chat/{username}. Skipped for a bare drive root (no parent to record as base).
  if (dirNameOf(picked) && !out.some((c) => samePath(c.root, picked))) {
    out.push({ root: trimTrailingSep(picked), base: parentBase });
  }
  return out;
}

const emptyRegistry = (): Registry => ({ version: 1, lastUsedId: null, accounts: [] });

// The account this SESSION operates on. Set once at boot (or on a switch, which reloads the app). Every
// per-account path/keychain name derives from it, mirroring the _dataRoot cache pattern.
let _activeId: string = DEFAULT_ACCOUNT_ID;
export const activeAccountId = (): string => _activeId;
export const setActiveAccountId = (id: string): void => { _activeId = id; };

// --- Per-account naming. `default` keeps the legacy fixed names; others are id-suffixed. -----------------

export function accountFileName(id: string): string {
  return id === DEFAULT_ACCOUNT_ID ? "aicc-account.json" : `aicc-account-${id}.json`;
}
export function pointerFileName(id: string): string {
  return id === DEFAULT_ACCOUNT_ID ? "aicc-data-dir.txt" : `aicc-data-dir-${id}.txt`;
}
/** OS keychain entry name for silent unlock. MUST stay "dek-v1" for the default account so existing users
 *  keep their stored key; additional accounts get their own entry. */
export function keychainAccountName(id: string): string {
  return id === DEFAULT_ACCOUNT_ID ? "dek-v1" : `dek-v1-${id}`;
}
/** Resolve an account's data root = `{basePath || Documents}/rin-chat/{username}`. */
export async function resolveDataRoot(entry: { username: string; basePath?: string }): Promise<string> {
  const base = entry.basePath?.trim() || (await documentDir());
  return dataRootFrom(base, entry.username);
}
/** The default root for a NOT-yet-registered account being created with `username` (base = Documents). */
export async function defaultDataRootFor(username: string): Promise<string> {
  return resolveDataRoot({ username });
}

// --- Registry read/write (plain JSON in the OS app-data dir) ---------------------------------------------

/** Set when the registry file exists but couldn't be read or parsed. A transient I/O error or a corrupt
 *  file used to read as "no accounts" — and the next write (legacy adoption, onboarding) then REPLACED the
 *  registry with one account, orphaning every other vault. While this is set, saves refuse. */
let registryUnreadable = false;

export async function loadRegistry(): Promise<Registry> {
  try {
    const raw = await invoke<string>("account_registry_read"); // "" = no file yet
    if (!raw.trim()) { registryUnreadable = false; return emptyRegistry(); }
    const r = JSON.parse(raw) as { version?: number; lastUsedId?: string | null; accounts?: unknown };
    registryUnreadable = false;
    if (!Array.isArray(r.accounts)) return emptyRegistry();
    // Normalize each entry — tolerant of shapes from the FIRST multi-account build, which stored `displayName`
    // and had no `username`/`basePath`. Without this, entry.username is undefined and folderSafe(...).trim()
    // throws on boot.
    const accounts: AccountEntry[] = (r.accounts as Array<Record<string, unknown>>)
      .filter((a) => a && typeof a.id === "string")
      .map((a) => ({
        id: String(a.id),
        username: String(a.username ?? a.displayName ?? a.id),
        basePath: typeof a.basePath === "string" ? a.basePath : "", // "" = un-migrated legacy layout
        createdAt: typeof a.createdAt === "number" ? a.createdAt : Date.now(),
        lastUsed: typeof a.lastUsed === "number" ? a.lastUsed : Date.now(),
      }));
    return { version: r.version ?? 1, lastUsedId: r.lastUsedId ?? null, accounts };
  } catch {
    registryUnreadable = true;
    return emptyRegistry();
  }
}
async function saveRegistry(r: Registry): Promise<void> {
  if (registryUnreadable) throw new Error(t`The account list (accounts.json in the app data folder) couldn't be read, so it won't be overwritten. Fix or remove that file and restart.`);
  await invoke<void>("account_registry_write", { contents: JSON.stringify(r, null, 2) });
}

export async function listAccounts(): Promise<AccountEntry[]> {
  return (await loadRegistry()).accounts;
}

/** Register (or update) an account and mark it most-recently-used. Idempotent on id. `basePath` "" = Documents. */
export async function upsertAccount(entry: { id: string; username: string; basePath?: string }, now: number): Promise<void> {
  const r = await loadRegistry();
  const existing = r.accounts.find((a) => a.id === entry.id);
  if (existing) {
    if (entry.username) existing.username = entry.username;
    if (entry.basePath !== undefined) existing.basePath = entry.basePath;
    existing.lastUsed = now;
  } else {
    r.accounts.push({ id: entry.id, username: entry.username || entry.id, basePath: entry.basePath ?? "", createdAt: now, lastUsed: now });
  }
  r.lastUsedId = entry.id;
  await saveRegistry(r);
}

/** Look up one registry entry by id. */
export async function getAccount(id: string): Promise<AccountEntry | undefined> {
  return (await loadRegistry()).accounts.find((a) => a.id === id);
}

/** Is `username` already taken by another account on this machine? Case-insensitive on the folder key so a
 *  case-only difference (which would collide on Windows/macOS) is rejected too. `exceptId` skips self (rename). */
export async function usernameTaken(username: string, exceptId?: string): Promise<boolean> {
  const key = usernameKey(username);
  return (await loadRegistry()).accounts.some((a) => a.id !== exceptId && usernameKey(a.username) === key);
}

/** Mark an account most-recently-used (on unlock), without changing its username/base. */
export async function touchAccount(id: string, now: number): Promise<void> {
  const r = await loadRegistry();
  const a = r.accounts.find((x) => x.id === id);
  if (a) a.lastUsed = now;
  r.lastUsedId = id;
  await saveRegistry(r);
}

/** Remove an account from the registry (does NOT delete its vault files — caller handles that). */
export async function removeAccountFromRegistry(id: string): Promise<void> {
  const r = await loadRegistry();
  r.accounts = r.accounts.filter((a) => a.id !== id);
  if (r.lastUsedId === id) r.lastUsedId = r.accounts[0]?.id ?? null;
  await saveRegistry(r);
}

/** A short, filesystem-safe id for a new account. */
export function newAccountId(): string {
  return crypto.randomUUID().slice(0, 8);
}

/**
 * One-time adoption: a pre-multi-account install has an `aicc-account.json` but no registry. Detect it and
 * seed the registry with the reserved "default" account so the existing vault appears in the picker without
 * moving a single file. Safe to call every boot — it no-ops once the registry exists or when there's no
 * legacy account (a genuinely fresh install goes straight to onboarding).
 */
export async function adoptLegacyAccountIfNeeded(): Promise<void> {
  const r = await loadRegistry();
  if (r.accounts.length > 0) return; // registry already established
  // Does the legacy default account file exist? Ask the vault layer via its status probe.
  const legacyPath = await join(await appLocalDataDir(), accountFileName(DEFAULT_ACCOUNT_ID));
  let hasLegacy = false;
  try {
    const st = await invoke<{ has_account: boolean; username: string | null }>("vault_status", { accountPath: legacyPath, keychainAccount: keychainAccountName(DEFAULT_ACCOUNT_ID) });
    hasLegacy = st.has_account;
    if (hasLegacy) {
      // Adopt with the account's real username; basePath "" (Documents). The boot layout migration moves its
      // data from the old flat location into rin-chat/{username} on this same first run.
      await upsertAccount({ id: DEFAULT_ACCOUNT_ID, username: st.username || "Account", basePath: "" }, Date.now());
    }
  } catch { /* no legacy account — leave the registry empty so boot routes to onboarding */ }
}
